/**
 * Action dispatcher — single source of truth for any agent-driven side
 * effect the embed app honours. Consumed by:
 *
 *   - CardRenderer            (button taps on card.actions[])
 *   - AppActionRenderer       (button or auto-fire on app_action items)
 *   - PageActionRenderer      (auto-fire on page_action items, routed to
 *                              the parent host page via parentBridge)
 *   - ChatView suggestions    (could be wired through here later)
 *
 * Two verb namespaces, kept in their own ChatContentItem types but unified
 * here so call-sites never branch:
 *
 *   App verbs (in-iframe state):
 *     navigate          args.view
 *     prefill_checkout  args.line_item, args.contact?, args.embedded_url?,
 *                       args.layout_on_open?  ('expanded' default; can be
 *                       'maximized' for full-screen partner pages or
 *                       'native' to suppress auto-promote on a per-card
 *                       basis — bootstrap still clamps against the
 *                       partner's theme.panel.layoutMax)
 *     submit_message    args.text, args.meta? (round-trips as a user reply)
 *     parent_event      args.event_type, args.* (any custom partner event)
 *     request_close     no args
 *     open_external     args.url
 *     set_layout        args.mode ∈ 'native' | 'expanded' | 'maximized'
 *                       (clamped by bootstrap against theme.panel.layoutMax)
 *
 *   Page verbs (parent host page DOM, routed via wg:action round-trip):
 *     highlight, scroll_into_view, focus, click, set_value, navigate,
 *     copy_to_clipboard, emit_event
 *
 * Legacy CardActionRef.kind values are aliased so older agent-emitted cards
 * keep working (open_url → open_external, send_message → submit_message,
 * book → prefill_checkout when args.line_item present).
 *
 * Unrecognised verbs log + no-op so an agent that ships a new verb ahead
 * of the client doesn't break the user's session.
 */
import type { AppActionVerb, CardActionRef, PageActionVerb } from '@wg/types';
import type { ResponseMeta } from './registry';
import type { ViewId } from '../context/ViewContext';
import type {
  CheckoutCartItem,
  CheckoutContact,
} from '../context/CheckoutContext';
import type {
  LayoutMode,
  PageActionResult,
  PageActionVerb as ParentPageActionVerb,
} from '../context/ParentBridgeContext';
import logger from '../utils/logger';

/**
 * Bag of dependencies the dispatcher needs. Built once per render in
 * ChatView and threaded through RenderCtx so renderers can call
 * dispatchAction(verb, args) without prop-drilling.
 */
export interface ActionDeps {
  setView: (v: ViewId) => void;
  setCart: (item: CheckoutCartItem | null) => void;
  setPrefillContact: (c: CheckoutContact | null) => void;
  onSendMessage: (text: string, meta?: ResponseMeta) => void;
  /**
   * Persist an item into ItineraryContext. Wired in ChatView from
   * useItinerary().addItem. Loosely typed (Record) so the dispatcher
   * can forward `add_to_itinerary` args without TypeScript needing to
   * import the full ItineraryItemInput shape.
   */
  addItineraryItem?: (
    input: Record<string, unknown> & { title: string },
  ) => unknown;
  /**
   * Remove an itinerary item by metadata.slug (preferred — stable join key
   * with the explore map) or normalized title. Wired in ChatView from
   * useItinerary(). Enables the explore↔widget TWO-WAY trip bridge:
   * removal used to be widget-only.
   */
  removeItineraryItem?: (ref: {
    activity_id?: string;
    provider?: string;
    slug?: string;
    title?: string;
  }) => unknown;
  /**
   * Mint (or return) the read-only share token. Wired from
   * useItinerary().shareItinerary. The minted token propagates back to the
   * host via the normal itinerary_updated sync event (SyncReporter includes
   * share_token), so this doesn't need its own response channel.
   */
  shareItinerary?: (rotate?: boolean) => Promise<string | null>;
  parentBridge: {
    send: (msg: { type: string; payload?: unknown }) => void;
    requestClose: () => void;
    dispatchAction: (
      verb: ParentPageActionVerb,
      args: Record<string, unknown>,
    ) => Promise<PageActionResult>;
    requestLayout: (mode: LayoutMode) => void;
    /** Bootstrap-reported cap (default 'expanded'). */
    layoutMax: LayoutMode;
  };
  /** Current view at dispatch time — used for view_changed analytics. */
  currentView: ViewId;
  /**
   * Fallback for prefill_checkout actions that arrive WITHOUT a fully-built
   * line_item — the brain emits the full shape because it pre-calls
   * /experiences/v1/{id}/book server-side, but admin-authored seed cards
   * (`persona.seed_content_items`) only know `{provider_id, product_id}`.
   * When set, the dispatcher calls this to construct the line_item before
   * navigating to checkout. Optional so legacy / synthetic ActionDeps
   * (tests, history-replay) don't have to provide it.
   *
   * Returns the canonical BookingInitResponse fields (`booking_type`, `url`,
   * `detection_patterns`) — same shape RN + main webapp see.
   */
  buildCheckoutFromProduct?: (body: {
    provider_id: string;
    product_id: string;
    activity_name?: string;
  }) => Promise<{
    booking_type: string;
    url: string;
    detection_patterns?: CheckoutCartItem['detection_patterns'];
  }>;
}

/** Set of known app verbs — used to disambiguate from legacy aliases. */
const APP_VERBS = new Set<
  AppActionVerb | 'add_to_itinerary' | 'remove_from_itinerary' | 'set_layout'
>([
  'navigate',
  'prefill_checkout',
  'submit_message',
  'parent_event',
  'request_close',
  'open_external',
  // Local-state verb (no AppActionVerb constant yet — added inline so the
  // dispatcher accepts it without a @wg/types churn). Persists an item
  // into ItineraryContext.
  'add_to_itinerary' as AppActionVerb,
  'remove_from_itinerary' as AppActionVerb,
  // Mints the read-only share token; the token rides back on the next
  // itinerary_updated sync event.
  'share_itinerary' as AppActionVerb,
  // Asks the bootstrap to swap the panel container layout. Capped by
  // theme.panel.layoutMax (DB-driven). args.mode ∈ 'native' | 'expanded'
  // | 'maximized'. Same channel ParentBridge.requestLayout uses.
  'set_layout' as AppActionVerb,
]);

const VALID_LAYOUT_MODES = new Set<LayoutMode>([
  'native',
  'expanded',
  'maximized',
]);

const PAGE_VERBS = new Set<PageActionVerb>([
  'highlight',
  'scroll_into_view',
  'focus',
  'click',
  'set_value',
  'navigate',
  'copy_to_clipboard',
  'emit_event',
]);

/**
 * Map legacy CardActionRef.kind values to the new AppActionVerb namespace.
 * Returns the verb string + a mutator that may augment args (e.g. promote
 * data.url to args.url for open_url → open_external).
 */
function aliasLegacyKind(
  kind: string,
  data: Record<string, unknown> | undefined,
): { verb: string; args: Record<string, unknown> } {
  const args = { ...(data || {}) };
  switch (kind) {
    case 'open_url':
      return { verb: 'open_external', args };
    case 'send_message':
      // data.text → args.text
      return { verb: 'submit_message', args };
    case 'book':
      // Treat "book" as a prefill_checkout when a line_item is provided;
      // otherwise navigate to the booking view.
      if (args.line_item) return { verb: 'prefill_checkout', args };
      return { verb: 'navigate', args: { view: 'booking' } };
    case 'add_to_itinerary':
      // Keep the verb as add_to_itinerary so the dispatcher branches into
      // ItineraryContext rather than just navigating. Args carry the
      // item shape (title, activity_id, provider, date, guests, …).
      return { verb: 'add_to_itinerary', args };
    case 'contact':
      // No first-class contact verb yet — fall through to parent_event so
      // the partner page can decide how to handle it.
      return {
        verb: 'parent_event',
        args: { event_type: 'contact_requested', ...args },
      };
    default:
      return { verb: kind, args };
  }
}

const VALID_VIEWS = new Set<ViewId>([
  'chat',
  'itinerary',
  'booking',
  'checkout',
]);

/**
 * Run an app-namespace verb against the iframe state.
 *
 * Always emits a wg:event upward when the action causes a view change so
 * partner host-page analytics can subscribe.
 */
export function executeAppAction(
  verb: string,
  rawArgs: Record<string, unknown> | undefined,
  deps: ActionDeps,
): void {
  // Run the legacy aliasing at the top so any legacy kinds funnel through
  // the canonical switch.
  const { verb: resolvedVerb, args } = aliasLegacyKind(verb, rawArgs);

  if (!APP_VERBS.has(resolvedVerb as AppActionVerb)) {
    logger.warn('[actions] unknown app verb', { verb: resolvedVerb });
    return;
  }

  switch (
    resolvedVerb as
      | AppActionVerb
      | 'add_to_itinerary'
      | 'remove_from_itinerary'
      | 'share_itinerary'
  ) {
    case 'navigate': {
      const target = args.view as ViewId | undefined;
      if (!target || !VALID_VIEWS.has(target)) {
        logger.warn('[actions] navigate: invalid view', { view: target });
        return;
      }
      const from = deps.currentView;
      deps.setView(target);
      emitViewChanged(deps, from, target, 'navigate');
      return;
    }

    case 'prefill_checkout': {
      let line = args.line_item as CheckoutCartItem | undefined;
      // Fallback for admin-authored seed cards: the simple shape
      // {product_id, provider, activity_name?} doesn't carry a line_item,
      // so we build one here by calling /chat/v1/{sid}/checkout-build —
      // the same endpoint the brain hits when it emits this action. The
      // dispatcher is sync so we kick the build off and re-enter the
      // case after it resolves; the visitor sees a brief no-op then the
      // checkout view opens. If buildCheckoutFromProduct isn't wired
      // (legacy ActionDeps), we fall through to the missing-line warning.
      if (!line || typeof line !== 'object' || !line.activity_name) {
        const productId =
          (args.product_id as string | undefined) ||
          (args.provider_product_id as string | undefined);
        const providerId =
          (args.provider as string | undefined) ||
          (args.provider_id as string | undefined);
        if (productId && providerId && deps.buildCheckoutFromProduct) {
          void (async () => {
            try {
              const built = await deps.buildCheckoutFromProduct!({
                provider_id: providerId,
                product_id: productId,
                activity_name:
                  (args.activity_name as string | undefined) ||
                  (args.title as string | undefined),
              });
              executeAppAction(
                'prefill_checkout',
                {
                  ...args,
                  line_item: {
                    activity_name:
                      (args.activity_name as string | undefined) ||
                      (args.title as string | undefined) ||
                      productId,
                    provider: providerId,
                    activity_id: productId,
                    embedded_url: built.url,
                    detection_patterns: built.detection_patterns,
                    requires_review: true,
                  } as unknown as CheckoutCartItem,
                },
                deps,
              );
            } catch (err) {
              logger.warn('[actions] prefill_checkout: build failed', {
                productId,
                providerId,
                err,
              });
            }
          })();
          return;
        }
        logger.warn('[actions] prefill_checkout: missing line_item', { args });
        return;
      }
      // Lift detection_patterns from args (the brain attaches them when
      // /catalog/v1/checkout-build returned them) into the cart item so
      // CheckoutView's iframe inspector can apply them. Provider name
      // never crosses into UI logic — only the rules.
      const detection_patterns =
        (args.detection_patterns as
          | CheckoutCartItem['detection_patterns']
          | undefined) || line.detection_patterns;
      // requires_review may live on line_item (brain-emitted card actions
      // set it there) or on args directly (legacy callers). Default true
      // for any embedded-iframe checkout so the visitor always gets the
      // WG-controlled review step before the partner page loads.
      const lineRecord = line as unknown as Record<string, unknown>;
      const requires_review =
        typeof lineRecord.requires_review === 'boolean'
          ? (lineRecord.requires_review as boolean)
          : (args.requires_review as boolean | undefined);
      const cartItem: CheckoutCartItem = {
        ...line,
        embedded_url:
          (args.embedded_url as string | undefined) || line.embedded_url,
        detection_patterns,
        requires_review,
        item_id:
          line.item_id ||
          `cart_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      };
      const contact = args.contact as CheckoutContact | undefined;
      deps.setCart(cartItem);
      if (contact) deps.setPrefillContact(contact);
      const from = deps.currentView;
      deps.setView('checkout');
      // Auto-promote the panel so the partner booking iframe gets enough
      // real estate. Default 'expanded' is a sensible cap-friendly value
      // for most checkouts; the brain can override with args.layout_on_open
      // when it knows the partner's checkout page benefits from more
      // (e.g. 'maximized' for a multi-step booking with calendar + party
      // size + payment all on one screen). Bootstrap clamps against
      // theme.panel.layoutMax — partners on 'native' silently keep the
      // compact panel regardless of what the brain asks for.
      if (deps.parentBridge.layoutMax !== 'native') {
        const requestedLayout = args.layout_on_open as LayoutMode | undefined;
        const target: LayoutMode =
          requestedLayout && VALID_LAYOUT_MODES.has(requestedLayout)
            ? requestedLayout
            : 'expanded';
        deps.parentBridge.requestLayout(target);
      }
      emitViewChanged(deps, from, 'checkout', 'prefill_checkout');
      return;
    }

    case 'add_to_itinerary': {
      // Persist into ItineraryContext. The dispatcher itself doesn't
      // navigate — staying on chat is the right default after the agent
      // saves something. Visitor still sees the Trip dot light up.
      logger.info('[actions] add_to_itinerary fired', {
        title: args.title || args.activity_name,
        activity_id: args.activity_id,
        provider: args.provider,
        has_dep: !!deps.addItineraryItem,
      });
      if (!deps.addItineraryItem) {
        logger.warn('[actions] add_to_itinerary: deps not wired');
        return;
      }
      const title = (args.title || args.activity_name) as string | undefined;
      if (!title) {
        logger.warn('[actions] add_to_itinerary: missing title', { args });
        return;
      }
      deps.addItineraryItem({
        ...args,
        title,
        status: args.status ?? 'saved',
      });
      return;
    }

    case 'remove_from_itinerary': {
      // Two-way trip bridge: the host page (explore map) or the agent can
      // remove a saved item, not just add. Resolution by metadata.slug
      // first (stable), title fallback.
      const ref = {
        activity_id: args.activity_id as string | undefined,
        provider: args.provider as string | undefined,
        slug: (args.slug ??
          (args.metadata as Record<string, unknown> | undefined)?.slug) as
          | string
          | undefined,
        title: (args.title || args.activity_name) as string | undefined,
      };
      logger.info('[actions] remove_from_itinerary fired', {
        ...ref,
        has_dep: !!deps.removeItineraryItem,
      });
      if (!deps.removeItineraryItem) {
        logger.warn('[actions] remove_from_itinerary: deps not wired');
        return;
      }
      if (!ref.activity_id && !ref.slug && !ref.title) {
        logger.warn('[actions] remove_from_itinerary: missing slug/title', {
          args,
        });
        return;
      }
      deps.removeItineraryItem(ref);
      return;
    }

    case 'share_itinerary': {
      // Host (explore) asked for a read-only share link. Mint it; the token
      // rides back on the next itinerary_updated event (SyncReporter), which
      // the host is already listening to — no dedicated response channel.
      logger.info('[actions] share_itinerary fired', {
        has_dep: !!deps.shareItinerary,
        rotate: !!args.rotate,
      });
      if (!deps.shareItinerary) {
        logger.warn('[actions] share_itinerary: deps not wired');
        return;
      }
      void deps.shareItinerary(!!args.rotate);
      return;
    }

    case 'submit_message': {
      const text = (args.text as string | undefined)?.trim();
      if (!text) {
        logger.warn('[actions] submit_message: missing text', { args });
        return;
      }
      const meta = args.meta as ResponseMeta | undefined;
      deps.onSendMessage(text, meta);
      return;
    }

    case 'parent_event': {
      const eventType = args.event_type as string | undefined;
      if (!eventType) {
        logger.warn('[actions] parent_event: missing event_type', { args });
        return;
      }
      deps.parentBridge.send({
        type: 'wg:event',
        payload: args,
      });
      return;
    }

    case 'request_close': {
      deps.parentBridge.requestClose();
      return;
    }

    case 'open_external': {
      const url = args.url as string | undefined;
      if (!url) {
        logger.warn('[actions] open_external: missing url', { args });
        return;
      }
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        logger.warn('[actions] open_external: window.open threw', err);
      }
      return;
    }

    case 'set_layout' as AppActionVerb: {
      const mode = args.mode as LayoutMode | undefined;
      if (!mode || !VALID_LAYOUT_MODES.has(mode)) {
        logger.warn('[actions] set_layout: invalid mode', { mode });
        return;
      }
      // Bootstrap clamps against theme.panel.layoutMax; if the partner has
      // capped at 'native' this no-ops visually but we still send so the
      // wg:layout_changed reply keeps the SPA's layoutMode in sync.
      deps.parentBridge.requestLayout(mode);
      return;
    }
  }
}

/**
 * Run a page-namespace verb against the partner host page. Always
 * proxies through parentBridge.dispatchAction → bootstrap's wg:action
 * handler. Resolves with the bootstrap's verdict; caller may use it for
 * analytics or to fall back when a mutating verb hits consent_required.
 */
export async function executePageAction(
  verb: string,
  rawArgs: Record<string, unknown> | undefined,
  deps: ActionDeps,
): Promise<PageActionResult> {
  if (!PAGE_VERBS.has(verb as PageActionVerb)) {
    logger.warn('[actions] unknown page verb', { verb });
    return { ok: false, error: 'verb_unknown' };
  }
  return deps.parentBridge.dispatchAction(
    verb as ParentPageActionVerb,
    rawArgs || {},
  );
}

/**
 * Combined dispatcher. Renderers don't need to know which namespace the
 * verb is in if they hold a typed content item — the content type
 * already encodes that — but CardActionRef.kind is loose, so this routes
 * by lookup.
 */
export function dispatchCardAction(
  action: CardActionRef,
  deps: ActionDeps,
): void {
  // CardActionRef.kind only carries app-namespace verbs (cards never
  // dispatch page actions in v1 — those come from page_action items).
  executeAppAction(action.kind, action.data, deps);
}

function emitViewChanged(
  deps: ActionDeps,
  from: ViewId,
  to: ViewId,
  cause: 'navigate' | 'prefill_checkout',
): void {
  if (from === to) return;
  deps.parentBridge.send({
    type: 'wg:event',
    payload: {
      event_type: 'view_changed',
      from,
      to,
      cause,
    },
  });
}
